import React, { useEffect } from 'react';

const PerformanceOptimizer: React.FC = () => {
  useEffect(() => {
    // Preload critical resources
    const preloadCriticalResources = () => {
      // Preload hero image
      const heroImage = new Image();
      heroImage.fetchPriority = 'high';
      heroImage.src = 'https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';
      
      // Preconnect to external domains
      const preconnectDomains = [
        'https://images.pexels.com',
        'https://vibncjluhzsuhymwumdt.supabase.co',
        'https://www.google.com',
        'https://www.googletagmanager.com',
        'https://connect.facebook.net',
        'https://platform.twitter.com',
        'https://assets.pinterest.com',
        'https://platform.linkedin.com'
      ];
      
      preconnectDomains.forEach(domain => {
        if (!document.querySelector(`link[rel="preconnect"][href="${domain}"]`)) {
          const link = document.createElement('link');
          link.rel = 'preconnect';
          link.href = domain;
          link.crossOrigin = 'anonymous';
          document.head.appendChild(link);
        }
      });
    };

    // Optimize images with Intersection Observer
    const optimizeImages = () => {
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
                img.classList.remove('lazy');
                observer.unobserve(img);
              }
            }
          });
        }, {
          rootMargin: '200px 0px', // Start loading images 200px before they appear in viewport
          threshold: 0.01
        });

        // Observe all images with data-src attribute
        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      } else {
        // Fallback for browsers that don't support IntersectionObserver
        document.querySelectorAll('img[data-src]').forEach(img => {
          const imgEl = img as HTMLImageElement;
          if (imgEl.dataset.src) {
            imgEl.src = imgEl.dataset.src;
          }
        });
      }
    };

    // Defer non-critical JavaScript
    const deferNonCriticalJS = () => {
      // Find scripts that should be deferred
      const scripts = Array.from(document.querySelectorAll('script:not([async]):not([defer])'));
      
      scripts.forEach(script => {
        const scriptEl = script as HTMLScriptElement;
        
        // Skip if it's already processed or is the main entry script
        if (scriptEl.hasAttribute('data-processed') || scriptEl.src.includes('main.tsx')) {
          return;
        }
        
        // Mark as processed
        scriptEl.setAttribute('data-processed', 'true');
        
        // Create a new script element with defer attribute
        if (scriptEl.src) {
          const newScript = document.createElement('script');
          newScript.src = scriptEl.src;
          newScript.defer = true;
          
          // Preserve all original attributes
          Array.from(scriptEl.attributes).forEach(attr => {
            if (attr.name !== 'src' && attr.name !== 'data-processed') {
              newScript.setAttribute(attr.name, attr.value);
            }
          });
          
          // Replace the old script with the new one
          scriptEl.parentNode?.replaceChild(newScript, scriptEl);
        }
      });
    };

    // Execute optimizations
    preloadCriticalResources();
    
    // Run image optimization after initial render
    setTimeout(optimizeImages, 100);
    
    // Defer non-critical JS
    setTimeout(deferNonCriticalJS, 200);

    // Service Worker registration for caching
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then(registration => {
            console.log('SW registered: ', registration);
          })
          .catch(registrationError => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
    
    // Add event listener for page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // When page becomes visible again, check for new images to lazy load
        optimizeImages();
      }
    });
  }, []);

  return null;
};

export default PerformanceOptimizer;